import { Component, type ReactNode } from 'react';

import { Icon } from '../shared';

// This package never calls `console.*` itself (matching `registry/cell-registry.ts`'s inference
// trace) — a consumer opts in by registering a handler, kept off (a no-op) until one does.
export type CellErrorHandler = (error: unknown) => void;

/**
 * One handler for the whole application, not one per grid — this is module state, so a second
 * `setCellErrorHandler` replaces the first rather than adding to it. That is the intended
 * cardinality: cell errors go to whatever the app reports errors with (Sentry, a logger), which is
 * chosen once at bootstrap, the same shape `setCellInferenceTrace` uses. Two independently-mounted
 * grids wanting *different* error sinks is not supported; if that need appears, this becomes a
 * prop or context rather than gaining registration order semantics nobody can reason about.
 */
let onCellError: CellErrorHandler | undefined;

export function setCellErrorHandler(handler: CellErrorHandler | undefined): void {
  onCellError = handler;
}

export interface CellErrorBoundaryProps {
  children: ReactNode;
}

interface CellErrorBoundaryState {
  hasError: boolean;
}

// Scoped to exactly one `<td>` — a single renderer throwing degrades that one cell, never the
// whole table.
export class CellErrorBoundary extends Component<CellErrorBoundaryProps, CellErrorBoundaryState> {
  override state: CellErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): CellErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown): void {
    onCellError?.(error);
  }

  override render() {
    if (this.state.hasError) {
      return <Icon name="warning" size="sm" aria-label="This cell failed to render" />;
    }
    return this.props.children;
  }
}
